import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { StatusPill } from "./Tournaments";
import { StreamEmbed, Standings } from "@/components/Stream";
import { buyStatePass } from "@/components/StatePass";
import { CODE_TO_NAME } from "@/lib/states";

export default function TournamentDetail() {
    const { slug } = useParams();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [eligibility, setEligibility] = useState(null);
    const [standings, setStandings] = useState(null);
    const [registering, setRegistering] = useState(false);

    const loadEligibility = () => {
        if (!user) { setEligibility(null); return; }
        api.get(`/tournaments/${slug}/eligibility`).then(r => setEligibility(r.data)).catch(() => setEligibility(null));
    };
    const loadStandings = () => {
        api.get(`/tournaments/${slug}/standings`).then(r => setStandings(r.data)).catch(() => setStandings(null));
    };

    useEffect(() => {
        api.get(`/tournaments/${slug}`).then(r => setData(r.data)).catch(() => setData({ tournament: null }));
        loadStandings();
        // eslint-disable-next-line
    }, [slug]);

    useEffect(() => { loadEligibility(); /* eslint-disable-next-line */ }, [slug, user]);

    // Poll standings every 8s if tournament is live
    useEffect(() => {
        if (!data?.tournament || data.tournament.status !== "live") return;
        const id = setInterval(loadStandings, 8000);
        return () => clearInterval(id);
        // eslint-disable-next-line
    }, [data?.tournament?.status, slug]);

    const register = async () => {
        if (!user) { toast.error("Sign in to register"); return; }
        if (eligibility && !eligibility.eligible) {
            toast.error(eligibility.reasons?.[0] || "Not eligible yet");
            return;
        }
        setRegistering(true);
        try {
            const r = await api.post(`/tournaments/${slug}/register`);
            toast.success(`${r.data.team} registered`);
            const fresh = await api.get(`/tournaments/${slug}`);
            setData(fresh.data);
            loadEligibility();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Registration failed");
        } finally { setRegistering(false); }
    };

    if (!data) return <div className="pt-32 text-center font-mono text-white/40">Loading circuit…</div>;
    if (!data.tournament) return <div className="pt-32 text-center font-display text-3xl">Tournament not found.</div>;
    const { tournament: t, matches, teams } = data;

    return (
        <div className="pt-[68px]" data-testid="tournament-detail">
            {/* Hero */}
            <section className="relative min-h-[70vh] overflow-hidden">
                <motion.div initial={{ scale: 1.1, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute inset-0 -z-10">
                    <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${t.banner_url})` }} />
                    <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/80 to-void" />
                    <div className="absolute inset-0 grid-lines opacity-30" />
                </motion.div>

                <div className="max-w-[1600px] mx-auto px-6 md:px-10 pt-32 pb-16">
                    <Link to="/tournaments" className="overline text-white/50 hover:text-volt transition-colors">← All tournaments</Link>
                    <div className="mt-6 flex items-center gap-4">
                        <div className="overline">{t.game}</div>
                        <StatusPill status={t.status} />
                    </div>
                    <h1 className="mt-6 font-display font-black tracking-tighter text-5xl md:text-8xl leading-[0.88] max-w-5xl">
                        {t.name}
                    </h1>
                    <p className="mt-6 text-secondary max-w-xl text-base md:text-lg leading-relaxed">{t.description}</p>

                    <div className="mt-16 grid grid-cols-2 md:grid-cols-5 border-t border-white/10">
                        {[
                            ["Prize pool", `$${t.prize_pool.toLocaleString()}`],
                            ["Format", t.format],
                            ["Team size", t.team_size],
                            ["Slots", `${t.registered_teams}/${t.max_teams}`],
                            ["Start", t.start_date],
                        ].map(([k, v], i) => (
                            <div key={k} className={`py-6 ${i > 0 ? "md:border-l border-white/10" : ""}`}>
                                <div className="overline text-white/40">{k}</div>
                                <div className="mt-2 font-display font-black text-lg md:text-2xl">{v}</div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-10">
                        {!user ? (
                            <Link to="/login" className="btn-primary" data-testid="login-to-register-link">Sign in to register →</Link>
                        ) : (
                            <EligibilityPanel eligibility={eligibility} onRegister={register} registering={registering} tournament={t} />
                        )}
                    </div>
                </div>
            </section>

            {/* Live room — stream + standings — only when tournament is live and has a stream */}
            {t.status === "live" && t.stream_url && (
                <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-4 pb-8 grid lg:grid-cols-12 gap-6" data-testid="tournament-live-room">
                    <div className="lg:col-span-8">
                        <div className="flex items-center justify-between mb-4">
                            <div className="overline">[ Live Room ]</div>
                            <Link to="/live" className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/60 hover:text-volt transition-colors">Open full live view →</Link>
                        </div>
                        <StreamEmbed platform={t.stream_platform} channel={t.stream_channel} url={t.stream_url} />
                    </div>
                    <div className="lg:col-span-4">
                        <div className="flex items-center justify-between mb-4">
                            <div className="overline">[ Live Standings ]</div>
                            <div className="flex items-center gap-2 font-mono text-[9px] tracking-[0.24em] uppercase text-white/40">
                                <span className="w-1 h-1 rounded-full bg-volt live-dot" /> Refresh 8s
                            </div>
                        </div>
                        <Standings rows={standings?.standings?.slice(0, 8) || []} advances={standings?.tournament?.advances} compact />
                    </div>
                </section>
            )}

            {/* Standings / Bracket — depending on tier */}
            {(t.bracket_tier === "qualifier" || t.bracket_tier === "free_cash") ? (
                <section className="max-w-[1600px] mx-auto px-6 md:px-10 section" data-testid="standings-section">
                    <div className="overline mb-4">[ Standings ] Rolling points</div>
                    <h2 className="font-display font-black tracking-tighter text-3xl md:text-5xl mb-4">{t.bracket_tier === "free_cash" ? "Weekly cash on the line." : "The cut for the semis."}</h2>
                    <p className="text-secondary max-w-xl mb-12">{t.bracket_tier === "free_cash" ? "Points update after every match. Top squads take the cash — free entry for State Pass holders." : `Points update after every match. The top ${t.advances} squads at close punch through to the semifinals.`}</p>
                    <Standings rows={standings?.standings || []} advances={standings?.tournament?.advances} />
                </section>
            ) : (
                <section className="max-w-[1600px] mx-auto px-6 md:px-10 section" data-testid="bracket-section">
                    <div className="overline mb-4">[ Bracket ]</div>
                    <h2 className="font-display font-black tracking-tighter text-3xl md:text-5xl mb-16">The road to the trophy.</h2>
                    {matches.length === 0 ? (
                        <div className="border border-white/10 clip-frame-sm p-16 text-center">
                            <div className="overline text-white/40 mb-3">Bracket pending</div>
                            <div className="font-display text-2xl">Seeds drop 24h before start.</div>
                        </div>
                    ) : (
                        <Bracket matches={matches} teams={teams} />
                    )}
                </section>
            )}
        </div>
    );
}

const teamName = (id, teams) => teams.find(t => t.id === id)?.tag || (id ? "TBD" : "—");

const Bracket = ({ matches, teams }) => {
    const rounds = Array.from(new Set(matches.map(m => m.round))).sort();
    const labels = ["Quarterfinals", "Semifinals", "Grand Final", "Championship"];
    return (
        <div className="overflow-x-auto pb-6">
            <div className="flex gap-6 min-w-max">
                {rounds.map((r, ri) => {
                    const roundMatches = matches.filter(m => m.round === r).sort((a, b) => a.slot - b.slot);
                    return (
                        <div key={r} className="flex flex-col justify-around gap-6 min-w-[260px]" style={{ paddingTop: ri * 40, paddingBottom: ri * 40 }}>
                            <div className="overline text-white/40">[ {String(r).padStart(2,'0')} ] {labels[ri] || `Round ${r}`}</div>
                            {roundMatches.map(m => (
                                <div key={m.id} className={`border clip-frame-sm p-4 bg-[#0A0A0A] ${m.status === "live" ? "border-signal/50" : m.status === "finished" ? "border-white/10" : "border-white/5"}`} data-testid={`match-${m.id}`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/40">Match {m.slot + 1}</div>
                                        {m.status === "live" && <span className="font-mono text-[10px] text-signal uppercase tracking-[0.2em] flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-signal live-dot" />Live</span>}
                                        {m.status === "finished" && <span className="font-mono text-[10px] text-white/40 uppercase">Final</span>}
                                    </div>
                                    <MatchRow team={teamName(m.team_a, teams)} score={m.score_a} winner={m.winner === m.team_a} />
                                    <div className="my-1 border-t border-white/5" />
                                    <MatchRow team={teamName(m.team_b, teams)} score={m.score_b} winner={m.winner === m.team_b} />
                                </div>
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const MatchRow = ({ team, score, winner }) => (
    <div className={`flex items-center justify-between py-2 ${winner ? "text-volt" : "text-white/80"}`}>
        <span className="font-display font-extrabold text-sm">{team}</span>
        <span className="font-mono text-sm">{score}</span>
    </div>
);

const EligibilityPanel = ({ eligibility, onRegister, registering, tournament }) => {
    const [buying, setBuying] = useState(false);
    if (!eligibility) {
        return <div className="font-mono text-xs text-white/40" data-testid="eligibility-loading">Checking eligibility…</div>;
    }
    const { checks, eligible, reasons } = eligibility;
    const order = ["state_match", "state_pass", "id_verified", "has_team", "bracket_open", "not_registered"];
    const needsPass = checks.state_pass && !checks.state_pass.ok;
    return (
        <div className="max-w-2xl border border-white/10 clip-frame bg-[#0A0A0A] p-6 md:p-7" data-testid="eligibility-panel">
            <div className="flex items-center justify-between mb-5">
                <div className="overline">[ Entry Requirements ]</div>
                <div className={`font-mono text-[10px] tracking-[0.24em] uppercase ${eligible ? "text-volt" : "text-signal"}`} data-testid="eligibility-status">
                    {eligible ? "◆ CLEAR TO ENTER" : "◇ NOT ELIGIBLE"}
                </div>
            </div>

            <ul className="space-y-3 mb-6">
                {order.filter(k => checks[k]).map((k) => {
                    const c = checks[k];
                    return (
                        <li key={k} className="flex items-start gap-4" data-testid={`eligibility-check-${k}`}>
                            <span className={`mt-[3px] w-4 h-4 flex-shrink-0 flex items-center justify-center border ${c.ok ? "border-volt bg-volt/10 text-volt" : "border-white/20 text-white/40"} font-mono text-[9px]`}>
                                {c.ok ? "✓" : "○"}
                            </span>
                            <div className="flex-1 min-w-0">
                                <div className={`font-mono text-xs tracking-[0.16em] uppercase ${c.ok ? "text-white" : "text-white/60"}`}>{c.label}</div>
                                {c.detail && (
                                    <div className={`mt-0.5 text-xs ${c.ok ? "text-white/50" : "text-white/40"}`}>{c.detail}</div>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>

            <button onClick={onRegister} disabled={!eligible || registering || tournament.status === "finished"}
                className="btn-primary w-full text-center disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="register-tournament-btn">
                {registering ? "Registering…" :
                    tournament.status === "finished" ? "Bracket closed" :
                    eligible ? "Register team →" : "Complete requirements to register"}
            </button>

            {/* Actionable hints */}
            {!eligible && reasons?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/5 flex flex-wrap items-center gap-3" data-testid="eligibility-hints">
                    {needsPass && (
                        <button
                            onClick={() => buyStatePass(setBuying)}
                            disabled={buying}
                            className="font-mono text-[10px] tracking-[0.24em] uppercase text-volt hover:text-white transition-colors disabled:opacity-40"
                            data-testid="eligibility-buy-pass-btn"
                        >
                            {buying ? "→ Opening checkout…" : "→ Get the $20 State Pass"}
                        </button>
                    )}
                    {!checks.id_verified?.ok && checks.id_verified?.status === "unverified" && (
                        <Link to="/register" className="font-mono text-[10px] tracking-[0.24em] uppercase text-volt hover:text-white transition-colors">
                            → Upload state ID
                        </Link>
                    )}
                    {!checks.has_team?.ok && (
                        <Link to="/teams" className="font-mono text-[10px] tracking-[0.24em] uppercase text-volt hover:text-white transition-colors">
                            → Create a {tournament.game} team
                        </Link>
                    )}
                    {!checks.state_match?.ok && (
                        <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/60" data-testid="eligibility-state-lock-hint">
                            ◆ Locked to {CODE_TO_NAME[tournament.region] || tournament.region} residents — your account is registered to another state
                        </span>
                    )}
                </div>
            )}
        </div>
    );
};

